import { AlkLogger, uuidv4 } from '@alk/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { IInstrumentsService, INSTRUMENTS_SERVICE } from '../../instruments';
import { inject, injectable } from 'inversify';
import { IQuoteResponse, IValuedStreamMeta, SpotPriceRequestWithClient } from '../../../interfaces';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { ISpotStreamAdapterService, SPOT_STREAM_ADAPTER_SERVICE } from '../../message-adapters/spot-stream-adapter';
import { ISubscriptionPoolingService } from './subscription-pooling.interface';
import { stringifyPlainProps } from '../../../helpers/object-utils';
import { takeUntil } from 'rxjs/operators';

@injectable()
export class SubscriptionPoolingService implements ISubscriptionPoolingService {
    @inject(SPOT_STREAM_ADAPTER_SERVICE) private readonly spotStreamAdapter: ISpotStreamAdapterService;
    @inject(INSTRUMENTS_SERVICE) private readonly instrumentsService: IInstrumentsService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    private shouldResubscribe$: BehaviorSubject<void>;
    private readonly subscriptions: Map<string, IValuedStreamMeta<string>> = new Map<string, IValuedStreamMeta<string>>();

    unsubscribe = (clientToken: string): void => {
        AlkLogger.debug(
            `[%cSUBSCRIPTION_POOLING%c#delete] Deleting ${ clientToken }: ${ this.subscriptions.get(clientToken)?.communicationToken }`,
            'background:green', '',
        );

        if (!this.subscriptions.has(clientToken)) {
            AlkLogger.debug(
                `[%cSUBSCRIPTION_POOLING%c#delete] %cIncorrect unsubscribe. Token: ${ clientToken } unknown`,
                'background:green', '', 'color:orange',
            );
            return;
        }

        const sub = this.subscriptions.get(clientToken);

        this.subscriptions.delete(clientToken);

        const subIsNoLongerInUse = Array.from(this.subscriptions.values()).every(
            subscription => subscription.communicationToken !== sub.communicationToken);

        if (subIsNoLongerInUse) {
            this.spotStreamAdapter.delete(sub.communicationToken);
        } else {
            AlkLogger.debug(
                `[%cSUBSCRIPTION_POOLING%c#delete] %cKeep subscription ${ sub.communicationToken }, as it is still in use`,
                'background:green', '', 'color:orange',
            );
        }

        AlkLogger.info(
            `[%cSUBSCRIPTION_POOLING%c#delete] %cAlive spot price feeds: ${ this.prettyPrintAliveFeeds(this.subscriptions) }`,
            'background:green', '', 'color:yellow',
        );
    };

    private readonly prettyPrintAliveFeeds = (subscriptions: Map<string, IValuedStreamMeta<string>>) =>
        Array.from(subscriptions.entries(), ([ clientToken, streamMetaData ]) =>
            `\n  ${ clientToken }: ${ streamMetaData.communicationToken }`,
        ).join('') || 'empty';

    create(request: SpotPriceRequestWithClient): string {
        return this.createOrGetExisting(request);
    }

    private createOrGetExisting(request: SpotPriceRequestWithClient, clientToken = uuidv4()): string {
        const requestStringifyId = stringifyPlainProps(request);

        AlkLogger.debug(
            `[%cSUBSCRIPTION_POOLING%c#createOrGetExisting] Creating ${ clientToken } for request ${ requestStringifyId }`,
            'background:green', '',
        );

        const existingSubscription = Array.from(this.subscriptions.values()).find(sub => sub.value === requestStringifyId);

        let streamMetaData: IValuedStreamMeta<string>;

        if (Boolean(existingSubscription)){
            AlkLogger.debug(
                `[%cSUBSCRIPTION_POOLING%c#createOrGetExisting] Reuse existing subscription ${ existingSubscription.communicationToken }`,
                'background:green', '',
            );

            streamMetaData = {
                clientToken,
                communicationToken: existingSubscription.communicationToken,
                value: requestStringifyId,
            };
        } else {
            const requestId = this.spotStreamAdapter.create(request);

            AlkLogger.info(
                `[%cSUBSCRIPTION_POOLING%c#createOrGetExisting] Create new subscription ${ requestId }`,
                'background:green', '',
            );

            streamMetaData = {
                clientToken,
                communicationToken: requestId,
                value: requestStringifyId,
            };
        }

        this.subscriptions.set(clientToken, streamMetaData);

        AlkLogger.info(
            `[%cSUBSCRIPTION_POOLING%c#createOrGetExisting] %cAlive spot price feeds: ${ this.prettyPrintAliveFeeds(this.subscriptions) }`,
            'background:green', '', 'color:yellow',
        );

        return clientToken;
    }

    spotPriceObservable(clientToken: string): Observable<IQuoteResponse> {
        const communicationToken = this.subscriptions.get(clientToken)?.communicationToken;
        return Boolean(communicationToken) ? this.spotStreamAdapter.priceObservable(communicationToken) : EMPTY;
    }

    shouldResubscribeObservable(): Observable<void> {
        if (!Boolean(this.shouldResubscribe$)) {
            this.init();
        }
        return this.shouldResubscribe$.asObservable();
    }

    private init(): void {
        this.shouldResubscribe$ = new BehaviorSubject<void>(null);

        this.instrumentsService.instrumentsObservable().pipe(
            takeUntil(this.sessionLifeCycle.destroyObservable()),
        ).subscribe(() => {
            this.clearAllSubscriptions();
            this.shouldResubscribe$.next();
        });
    }

    private readonly clearAllSubscriptions = (): void => {
        for (const clientToken of this.subscriptions.keys()) {
            this.unsubscribe(clientToken);
        }
    };
}
